
import { useEditor, EditorContent, BubbleMenu, FloatingMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading1,
  Heading2,
  Heading3,
  Pilcrow,
  Redo,
  Undo,
  Palette,
  ExternalLink,
  Trash2
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import './RichTextEditor.css';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

// Change from default export to named export
export const RichTextEditor = ({ content, onChange, placeholder = 'Write something amazing...', disabled = false }: RichTextEditorProps) => {
  // Separate state for each popover to avoid conflicts
  const [linkUrl, setLinkUrl] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [editLinkUrl, setEditLinkUrl] = useState('');
  const [editLinkOpen, setEditLinkOpen] = useState(false);

  // Define primary and secondary colors based on CSS variables
  const getPrimaryColor = () => {
    const primaryHSL = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
    // For a more accurate conversion, you would need a proper HSL to HEX converter
    return "#4c6ef5"; // Default primary color as fallback
  };

  const getSecondaryColor = () => {
    const secondaryHSL = getComputedStyle(document.documentElement).getPropertyValue('--secondary').trim();
    return "#e9ecef"; // Default secondary color as fallback
  };

  // Common text colors for the color picker with primary and secondary at the top
  const textColors = [
    getPrimaryColor(), // Primary color
    getSecondaryColor(), // Secondary color
    '#000000', // Black
    '#ffffff', // White
    '#868e96', // Gray
    '#fa5252', // Red
    '#e64980', // Pink
    '#be4bdb', // Grape
    '#7950f2', // Violet
    '#4c6ef5', // Indigo
    '#228be6', // Blue
    '#15aabf', // Cyan
    '#12b886', // Teal
    '#40c057', // Green
    '#fab005', // Yellow
    '#fd7e14', // Orange
  ];

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Exclude features we don't want
        codeBlock: false,
        // Ensure we have all other needed extensions enabled with proper config objects
        heading: {},
        bulletList: {},
        orderedList: {},
        bold: {},
        italic: {},
      }),
      Underline,
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: 'left', // Set default alignment to left
      }),
    ],
    content,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update editor content when content prop changes (e.g., when loading a post)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // Function to add a new link
  const addLink = useCallback(() => {
    if (!editor) return;
    
    // If text is selected, apply link to the selection
    if (editor.state.selection.empty) {
      toast.error('Please select text to add a link');
      return;
    }

    if (linkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
      
      setLinkUrl('');
      setLinkOpen(false);
    }
  }, [editor, linkUrl]);

  // Function to edit an existing link
  const updateLink = useCallback(() => {
    if (!editor) return;
    
    if (editLinkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: editLinkUrl })
        .run();
      
      setEditLinkUrl('');
      setEditLinkOpen(false);
      toast.success('Link updated');
    }
  }, [editor, editLinkUrl]);

  // Function to remove an existing link
  const removeLink = useCallback(() => {
    if (!editor) return;
    
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .unsetLink()
      .run();
    
    setEditLinkOpen(false);
    toast.success('Link removed');
  }, [editor]);

  // Function to set text color
  const setTextColor = useCallback((color: string) => {
    if (!editor) return;
    
    editor
      .chain()
      .focus()
      .setColor(color)
      .run();
  }, [editor]);

  // Function to check if a link exists at the current selection
  const checkForLink = useCallback(() => {
    if (!editor) return false;
    
    return editor.isActive('link');
  }, [editor]);

  // Get link attributes for the current selection
  const getLinkAttributes = useCallback(() => {
    if (!editor) return { href: '' };
    
    return editor.getAttributes('link');
  }, [editor]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K or Cmd+K to add link
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        
        if (checkForLink()) {
          // Edit existing link
          const attrs = getLinkAttributes();
          setEditLinkUrl(attrs.href || '');
          setEditLinkOpen(true);
        } else {
          // Add new link
          setLinkOpen(true);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [checkForLink, getLinkAttributes]);

  // Handle link management
  const handleLinkClick = useCallback(() => {
    if (!editor) return;
    
    // If a link is already active at the cursor position, open edit mode
    if (checkForLink()) {
      const attrs = getLinkAttributes();
      setEditLinkUrl(attrs.href || '');
      setEditLinkOpen(true);
    } else {
      // Otherwise, open the add link popover
      setLinkOpen(true);
    }
  }, [editor, checkForLink, getLinkAttributes]);

  if (!editor) {
    return null;
  }

  return (
    <div className="relative border rounded-md">
      {editor && (
        <BubbleMenu 
          editor={editor}
          tippyOptions={{ duration: 100 }}
          className="bg-white shadow-md rounded-md p-1 flex items-center border gap-1"
        >
          <Toggle
            size="sm"
            pressed={editor.isActive('bold')}
            onPressedChange={() => editor.chain().focus().toggleBold().run()}
            aria-label="Bold"
          >
            <Bold className="h-4 w-4" />
          </Toggle>
          <Toggle
            size="sm"
            pressed={editor.isActive('italic')}
            onPressedChange={() => editor.chain().focus().toggleItalic().run()}
            aria-label="Italic"
          >
            <Italic className="h-4 w-4" />
          </Toggle>
          <Toggle
            size="sm"
            pressed={editor.isActive('underline')}
            onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
            aria-label="Underline"
          >
            <UnderlineIcon className="h-4 w-4" />
          </Toggle>
          
          {/* Link button in bubble menu */}
          <Toggle
            size="sm"
            pressed={editor.isActive('link')}
            onPressedChange={handleLinkClick}
            aria-label="Link"
          >
            <LinkIcon className="h-4 w-4" />
          </Toggle>
        </BubbleMenu>
      )}

      <div className="flex flex-wrap gap-1 p-1 border-b bg-slate-50">
        <TooltipProvider>
          <div className="flex items-center mr-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  size="sm"
                  pressed={editor.can().undo()}
                  onPressedChange={() => editor.chain().focus().undo().run()}
                  disabled={disabled || !editor.can().undo()}
                  aria-label="Undo"
                >
                  <Undo className="h-4 w-4" />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Undo</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  size="sm"
                  pressed={editor.can().redo()}
                  onPressedChange={() => editor.chain().focus().redo().run()}
                  disabled={disabled || !editor.can().redo()}
                  aria-label="Redo"
                >
                  <Redo className="h-4 w-4" />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Redo</TooltipContent>
            </Tooltip>
          </div>

          <div className="w-px h-6 bg-slate-300 mx-1"></div>

          <div className="flex items-center mr-2">
            <ToggleGroup type="single" value={
              editor.isActive('heading', { level: 1 }) ? 'h1' :
              editor.isActive('heading', { level: 2 }) ? 'h2' :
              editor.isActive('heading', { level: 3 }) ? 'h3' :
              editor.isActive('paragraph') ? 'p' : ''
            }>
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem 
                    value="h1" 
                    size="sm"
                    aria-label="Heading 1"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    disabled={disabled}
                    data-state={editor.isActive('heading', { level: 1 }) ? 'on' : 'off'}
                    className="button"
                  >
                    <Heading1 className="h-4 w-4" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>Heading 1</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem 
                    value="h2" 
                    size="sm"
                    aria-label="Heading 2"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    disabled={disabled}
                    data-state={editor.isActive('heading', { level: 2 }) ? 'on' : 'off'}
                    className="button"
                  >
                    <Heading2 className="h-4 w-4" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>Heading 2</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem 
                    value="h3" 
                    size="sm"
                    aria-label="Heading 3"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    disabled={disabled}
                    data-state={editor.isActive('heading', { level: 3 }) ? 'on' : 'off'}
                    className="button"
                  >
                    <Heading3 className="h-4 w-4" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>Heading 3</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem 
                    value="p" 
                    size="sm"
                    aria-label="Paragraph"
                    onClick={() => editor.chain().focus().setParagraph().run()}
                    disabled={disabled}
                    data-state={editor.isActive('paragraph') ? 'on' : 'off'}
                    className="button"
                  >
                    <Pilcrow className="h-4 w-4" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>Paragraph</TooltipContent>
              </Tooltip>
            </ToggleGroup>
          </div>

          <div className="w-px h-6 bg-slate-300 mx-1"></div>

          <div className="flex items-center mr-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  size="sm"
                  pressed={editor.isActive('bold')}
                  onPressedChange={() => editor.chain().focus().toggleBold().run()}
                  disabled={disabled}
                  aria-label="Bold"
                >
                  <Bold className="h-4 w-4" />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Bold</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  size="sm"
                  pressed={editor.isActive('italic')}
                  onPressedChange={() => editor.chain().focus().toggleItalic().run()}
                  disabled={disabled}
                  aria-label="Italic"
                >
                  <Italic className="h-4 w-4" />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Italic</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  size="sm"
                  pressed={editor.isActive('underline')}
                  onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
                  disabled={disabled}
                  aria-label="Underline"
                >
                  <UnderlineIcon className="h-4 w-4" />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Underline</TooltipContent>
            </Tooltip>
          </div>

          <div className="w-px h-6 bg-slate-300 mx-1"></div>

          <div className="flex items-center mr-2">
            <Popover open={colorPickerOpen} onOpenChange={setColorPickerOpen}>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <Toggle
                        size="sm"
                        disabled={disabled}
                        aria-label="Text Color"
                      >
                        <Palette className="h-4 w-4" />
                      </Toggle>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent>Text Color</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <PopoverContent className="w-64 p-2">
                <div className="space-y-2">
                  <Label>Text Color</Label>
                  <div className="grid grid-cols-8 gap-1">
                    {textColors.map((color, index) => (
                      <div key={color} className="relative">
                        <button
                          className={`w-6 h-6 rounded-md border border-slate-200 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary ${index < 2 ? 'mb-6' : ''}`}
                          style={{ backgroundColor: color }}
                          onClick={() => setTextColor(color)}
                          title={index === 0 ? 'Primary' : index === 1 ? 'Secondary' : color}
                        />
                        {index < 2 && (
                          <span className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 text-xs whitespace-nowrap">
                            {index === 0 ? 'Primary' : 'Secondary'}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <Label htmlFor="custom-color">Custom Color</Label>
                    <Input
                      id="custom-color"
                      type="color"
                      className="h-8 w-full"
                      onChange={(e) => setTextColor(e.target.value)}
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="w-px h-6 bg-slate-300 mx-1"></div>

          <div className="flex items-center mr-2">
            <ToggleGroup type="single" value={
              editor.isActive({ textAlign: 'left' }) ? 'left' : 
              editor.isActive({ textAlign: 'center' }) ? 'center' : 
              editor.isActive({ textAlign: 'right' }) ? 'right' : 'left'
            }>
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem 
                    value="left" 
                    size="sm"
                    aria-label="Align Left"
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    disabled={disabled}
                    data-state={editor.isActive({ textAlign: 'left' }) ? 'on' : 'off'}
                    className="button"
                  >
                    <AlignLeft className="h-4 w-4" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>Align Left</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem 
                    value="center" 
                    size="sm"
                    aria-label="Align Center"
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    disabled={disabled}
                    data-state={editor.isActive({ textAlign: 'center' }) ? 'on' : 'off'}
                    className="button"
                  >
                    <AlignCenter className="h-4 w-4" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>Align Center</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem 
                    value="right" 
                    size="sm"
                    aria-label="Align Right"
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    disabled={disabled}
                    data-state={editor.isActive({ textAlign: 'right' }) ? 'on' : 'off'}
                    className="button"
                  >
                    <AlignRight className="h-4 w-4" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>Align Right</TooltipContent>
              </Tooltip>
            </ToggleGroup>
          </div>

          <div className="w-px h-6 bg-slate-300 mx-1"></div>

          <div className="flex items-center mr-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  size="sm"
                  pressed={editor.isActive('bulletList')}
                  onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
                  disabled={disabled}
                  aria-label="Bullet List"
                >
                  <List className="h-4 w-4" />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Bullet List</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  size="sm"
                  pressed={editor.isActive('orderedList')}
                  onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
                  disabled={disabled}
                  aria-label="Ordered List"
                >
                  <ListOrdered className="h-4 w-4" />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Ordered List</TooltipContent>
            </Tooltip>
          </div>

          <div className="w-px h-6 bg-slate-300 mx-1"></div>

          <div className="flex items-center">
            {/* Link button in toolbar - will use the same handler as the bubble menu link button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  size="sm"
                  pressed={editor.isActive('link')}
                  onPressedChange={handleLinkClick}
                  disabled={disabled}
                  aria-label="Link"
                >
                  <LinkIcon className="h-4 w-4" />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Link (Ctrl+K)</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      {/* Add Link Popover - shown when creating a new link */}
      <Popover open={linkOpen} onOpenChange={setLinkOpen}>
        <PopoverContent className="w-80">
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="link-url">URL</Label>
              <Input 
                id="link-url" 
                placeholder="https://example.com" 
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addLink();
                  }
                }}
                autoFocus
              />
            </div>
            <Button onClick={addLink} disabled={!linkUrl}>Add Link</Button>
          </div>
        </PopoverContent>
      </Popover>
      
      {/* Edit Link Popover - shown when editing an existing link */}
      <Popover open={editLinkOpen} onOpenChange={setEditLinkOpen}>
        <PopoverContent className="w-80">
          <div className="grid gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-link-url">Edit Link</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 px-2 text-red-500 hover:text-red-700"
                  onClick={removeLink}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
              <Input 
                id="edit-link-url" 
                placeholder="https://example.com" 
                value={editLinkUrl}
                onChange={e => setEditLinkUrl(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    updateLink();
                  }
                }}
                autoFocus
              />
            </div>
            <div className="flex justify-between">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  window.open(getLinkAttributes().href, '_blank');
                }}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Open Link
              </Button>
              <Button size="sm" onClick={updateLink} disabled={!editLinkUrl}>Update Link</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <EditorContent
        editor={editor}
        className="p-4 prose max-w-none min-h-[300px]"
        disabled={disabled}
        tabIndex={0} // Make sure the editor can receive focus when tabbing
      />
    </div>
  );
};

